import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { DictateMic, type DictateOpts } from './DictateMic.js';
import { commentSub, type DraftComment } from '../lib/noteReview.js';
import type { CardState } from '../lib/cardStates.js';
import { hasChanges, wordDiff, type DiffPart, type EditHunk } from '../lib/trackChanges.js';

/**
 * ehr#277 - tracked changes on a progress note; ehr#571 (reopen 10-23, PROGRESS-NOTE V141 V83)
 * moved them into the note itself. The supervisor edits each box in place (the box holds their
 * copy, a read-only "What <author> will see" box under it shows the added words green and the
 * removed words red) and comments in a margin beside the section, opened with the conversation
 * icon in its header. Back with the author, each box shows the changes under it and the margin
 * holds one card per edit (Keep / Revert) plus the supervisor's comments.
 * ehr#571 (reopen 10-24, points 3-5): V141's comment card (Cancel / Comment), comments on words
 * selected in a box (highlighted in it), and General comments in the first section's margin.
 */

/** The reviewer's working copy: edited section text and the comments posted so far. */
export interface ReviewDraft {
  sections: Record<string, string>;
  comments: DraftComment[];
}

export function DiffText({ parts }: { parts: DiffPart[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.kind === 'del' ? (
          <del key={i} className="tc-del">
            {p.text}
          </del>
        ) : p.kind === 'add' ? (
          <ins key={i} className="tc-add">
            {p.text}
          </ins>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

/** The conversation (speech bubble) icon. */
export function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" focusable="false">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The section header's comment button: the conversation icon, with a count once there is one. */
export function CommentButton({
  label,
  count,
  on,
  onClick,
}: {
  label: string;
  count: number;
  on?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`tc-cmt-ic${on ? ' on' : ''}`}
      aria-label={count ? `Comments on ${label} (${count})` : `Comment on ${label}`}
      title={count ? `Comments on ${label}` : `Comment on ${label}`}
      onClick={onClick}
    >
      <ChatIcon />
      {count > 0 && <span className="tc-cmt-n">{count}</span>}
    </button>
  );
}

const Key = () => (
  <span className="tc-key" aria-hidden>
    <ins className="tc-add">added</ins>
    <del className="tc-del">deleted</del>
  </span>
);

const Svg = ({ d, w = 14 }: { d: string; w?: number }) => (
  <svg viewBox="0 0 16 16" width={w} height={w} aria-hidden="true" focusable="false">
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const PENCIL = 'M11.4 2.4l2.2 2.2L6 12.2l-2.8.6.6-2.8z';
const DOWN = 'M8 3v8.5M4.5 8L8 11.5 11.5 8';
const UP = 'M8 13V4.5M4.5 8L8 4.5 11.5 8';
const EYE =
  'M1.6 8S4 3.7 8 3.7 14.4 8 14.4 8 12 12.3 8 12.3 1.6 8 1.6 8zM9.7 8a1.7 1.7 0 1 1-3.4 0 1.7 1.7 0 0 1 3.4 0z';
const LOCK =
  'M4.9 7h6.2a1.3 1.3 0 0 1 1.3 1.3v3.4a1.3 1.3 0 0 1-1.3 1.3H4.9a1.3 1.3 0 0 1-1.3-1.3V8.3A1.3 1.3 0 0 1 4.9 7zM5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7';

/**
 * ehr#571 (reopen 10-24, V141 V83.3): above an edited box, "Edit the note", so the box reads as the
 * place to type and the panel under it as the preview.
 */
export function EditLabel() {
  return (
    <div className="tc-edit-lbl">
      <Svg d={PENCIL} />
      Edit the note
    </div>
  );
}

/** The section header's edit count ("1 edit"), while the box has changes. */
export function EditChip({ text }: { text: string | null }) {
  return text ? <span className="tc-ed-chip">{text}</span> : null;
}

/**
 * Under an edited box (V141 V83.3): a connector ("Updates as you type") and the changes, read only,
 * in a dashed panel: "What <author> will see" while the supervisor types, "Changes from
 * <supervisor>" for the author. Clicking the preview puts the cursor back in the box above.
 */
export function ChangesBox({
  before,
  after,
  title,
  live,
}: {
  before: string;
  after: string;
  title: string;
  /** The person is typing this change now: show the "Updates as you type" connector. */
  live?: boolean;
}) {
  const [hint, setHint] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const parts = wordDiff(before, after);
  if (before === after || !hasChanges(parts)) return null;
  const toBox = () => {
    const box = wrap.current
      ?.closest('.pn-field, .pn-sec, [data-pn-target]')
      ?.querySelector<HTMLTextAreaElement>('.pn-ta-wrap textarea');
    if (!box) return;
    box.focus({ preventScroll: false });
    box.classList.remove('tc-pulse');
    void box.offsetWidth;
    box.classList.add('tc-pulse');
    setHint(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHint(false), 3000);
  };
  return (
    <div className="tc-ed-stack" ref={wrap}>
      {live && (
        <div className="tc-ed-conn" aria-hidden="true">
          <span className="tc-ed-conn-line" />
          <span className="tc-ed-conn-pill">
            <Svg d={DOWN} w={13} />
            Updates as you type
          </span>
        </div>
      )}
      <div
        className="tc-ed-diff"
        role="region"
        aria-label={`${title}, read only`}
        tabIndex={-1}
        onClick={toBox}
      >
        <div className="tc-ed-diff-top">
          <span className="tc-ed-diff-h">
            <Svg d={EYE} />
            <span>{title}</span>
          </span>
          <span className="tc-ed-diff-side">
            <Key />
            <span className="tc-ro">
              <Svg d={LOCK} w={12} />
              Read only
            </span>
          </span>
        </div>
        <div className="tc-ed-diff-b">
          <DiffText parts={parts} />
        </div>
        {hint && (
          <div className="tc-ed-hint" role="status">
            <Svg d={UP} />
            <span>
              <b>This is a preview.</b> Your cursor moved to the box above, where you make changes.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w) && !/^(dr|mr|mrs|ms)\.?$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

/**
 * ehr#741 (Chase 10-30, point 7): the person's Provider Profile photo from the EHR, else their
 * initials (V141's coloured square).
 */
export function Avatar({ name, photo, small }: { name: string; photo?: string; small?: boolean }) {
  return (
    <span className={`tc-av${small ? ' sm' : ''}`} aria-hidden>
      {photo ? <img src={photo} alt="" /> : initials(name)}
    </span>
  );
}

const CHECK = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
  </svg>
);
const XIC = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" />
  </svg>
);

/**
 * ehr#741 (Chase 10-30, points 1A, 1B, 1E; PROGRESS-NOTE V141 .gc-h): a review card's top row:
 * the avatar, the name (with "Field change", "Edit" or the time under it), the section's name on
 * the same row, and in the corner the green check (keep / resolve) and the red X (revert / reject
 * / delete).
 * ehr#741 (Chase 10-31, V132 part 2): data-cm-keep and data-cm-deny on the check and the X; a
 * field-change card's X is pressed (aria-pressed) once the change is not kept.
 */
export function CardHead({
  who,
  photo,
  sub,
  tag,
  about,
  onKeep,
  keepLabel = 'Keep',
  kept,
  onX,
  xLabel = 'Revert',
  denied,
}: {
  who: string;
  photo?: string;
  sub?: string;
  tag?: string;
  /** What the buttons act on, for their names (else the tag, else the person). */
  about?: string;
  onKeep?: () => void;
  keepLabel?: string;
  /** Ticked already: the check shows as on, and takes the tick off. */
  kept?: boolean;
  onX?: () => void;
  xLabel?: string;
  /** A field-change card turned down: the X shows as pressed. Leave out on other cards. */
  denied?: boolean;
}) {
  const what = about ?? tag ?? who;
  return (
    <div className="tc-mc-h">
      <Avatar name={who} photo={photo} />
      <div className="tc-mc-who">
        <b>{who}</b>
        {sub && <span>{sub}</span>}
      </div>
      {tag && (
        <span className="tc-tag" title={tag}>
          {tag}
        </span>
      )}
      {(onKeep || onX) && (
        <span className="tc-ic">
          {onKeep && (
            <button
              type="button"
              className={`tc-keep${kept ? ' on' : ''}`}
              aria-label={kept ? `Reopen: ${what}` : `${keepLabel}: ${what}`}
              aria-pressed={kept}
              data-cm-keep=""
              title={kept ? 'Reopen' : keepLabel}
              onClick={onKeep}
            >
              {CHECK}
            </button>
          )}
          {onX && (
            <button
              type="button"
              className={`tc-revert${denied ? ' on' : ''}`}
              aria-label={`${xLabel}: ${what}`}
              aria-pressed={denied}
              data-cm-deny=""
              title={xLabel}
              onClick={onX}
            >
              {XIC}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/** One reply on a card, as the card shows it. */
export interface ShownReply {
  key: string;
  who: string;
  photo?: string;
  when?: string;
  text: string;
}

/**
 * ehr#741 (Chase 10-30, points 1C and 6; V141 .gc-rep / .gc-reply): the replies under a card and
 * the Reply box with its Reply button (Enter sends too). Either side replies while the note is
 * theirs to work on.
 */
export function CardReplies({
  replies,
  onReply,
  label,
}: {
  replies: ShownReply[];
  /** Present while the person can reply. */
  onReply?: (text: string) => void;
  label: string;
}) {
  const [text, setText] = useState('');
  const send = () => {
    const t = text.trim();
    if (!t || !onReply) return;
    onReply(t);
    setText('');
  };
  return (
    <>
      {replies.map((r) => (
        <div key={r.key} className="tc-rep">
          <div className="tc-mc-h">
            <Avatar name={r.who} photo={r.photo} small />
            <div className="tc-mc-who">
              <b>{r.who}</b>
              {r.when && <span>{r.when}</span>}
            </div>
          </div>
          <p className="tc-mc-text">{r.text}</p>
        </div>
      ))}
      {onReply && (
        <div className="tc-reply">
          <input
            className="input"
            value={text}
            placeholder="Reply"
            aria-label={`Reply on ${label}`}
            maxLength={2000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                send();
              }
            }}
          />
          <button type="button" className="btn primary" disabled={!text.trim()} onClick={send}>
            Reply
          </button>
        </div>
      )}
    </>
  );
}

/**
 * ehr#571 (reopen 10-24, point 3, V141): a new comment, typed in the margin: who, what it is on
 * (the whole section, the selected words, or General), the box, then Cancel and Comment.
 * Comment (or Ctrl+Enter) posts it to the margin; it goes to the author with Request changes.
 */
export function CommentComposer({
  who,
  photo,
  label,
  section,
  quote,
  value,
  onChange,
  onCancel,
  onPost,
  focus,
  dictate,
}: {
  who: string;
  photo?: string;
  /** The section's name for the tag; none on a General comment. */
  label?: string;
  section: string;
  quote?: string;
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onPost: () => void;
  /** Put the cursor in the box (it was just opened). */
  focus?: boolean;
  dictate?: DictateOpts;
}) {
  const ta = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (focus) ta.current?.focus({ preventScroll: true });
  }, [focus]);
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (value.trim()) onPost();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };
  const what = label ? `Comment on ${label}` : 'General comment';
  return (
    <div className="tc-mc tc-mc-new" data-tc-composer={section}>
      <CardHead who={who} photo={photo} sub={commentSub(section, quote)} tag={label} />
      <div className="tc-mc-box">
        <textarea
          ref={ta}
          className="tc-mc-input"
          value={value}
          placeholder="Comment"
          aria-label={what}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
        />
        {dictate && <DictateMic dictate={dictate} label={what} value={value} onText={onChange} />}
      </div>
      <div className="tc-mc-btns">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={!value.trim()} onClick={onPost}>
          Comment
        </button>
      </div>
    </div>
  );
}

/**
 * A comment on the note (V141 comment card): one the reviewer posted and hasn't sent (the red X
 * deletes it), or one sent with the note (the green check resolves it). A comment on selected
 * words quotes them; replies and the Reply box sit under it (ehr#741).
 */
export function CommentCard({
  who,
  photo,
  label,
  text,
  when,
  quote,
  onDelete,
  onShow,
  onResolve,
  resolved,
  replies = [],
  onReply,
}: {
  who: string;
  photo?: string;
  /** The section's name for the tag; none on a General comment. */
  label?: string;
  text: string;
  when?: string;
  quote?: string;
  onDelete?: () => void;
  /** Clicking the card shows the words it is on. */
  onShow?: () => void;
  /** ehr#741: the green check (resolve, or reopen once resolved). */
  onResolve?: () => void;
  resolved?: boolean;
  replies?: ShownReply[];
  onReply?: (text: string) => void;
}) {
  return (
    <div
      className={`tc-mc${onShow ? ' tc-mc-click' : ''}${resolved ? ' resolved' : ''}`}
      data-cm-card=""
      onClick={(e) => {
        if (onShow && !(e.target as HTMLElement).closest('button, input')) onShow();
      }}
    >
      <CardHead
        who={who}
        photo={photo}
        sub={resolved ? 'Resolved' : when}
        tag={label}
        onKeep={onResolve}
        keepLabel="Resolve"
        kept={resolved}
        onX={onDelete}
        xLabel="Delete comment"
      />
      {quote && <div className="tc-quote">&ldquo;{quote}&rdquo;</div>}
      <p className="tc-mc-text">{text}</p>
      <CardReplies
        replies={replies}
        onReply={resolved ? undefined : onReply}
        label={label ?? 'the comment'}
      />
    </div>
  );
}

/**
 * ehr#571 (reopen 10-24, point 4, V141 V92): the dark "Comment" button that follows a selection in
 * a box. It keeps the selection (no focus change on press) and opens a comment on those words.
 */
export function SelectionPill({ x, y, onClick }: { x: number; y: number; onClick: () => void }) {
  return (
    <button
      type="button"
      className="tc-sel-pop"
      style={{
        left: Math.max(60, Math.min(x, window.innerWidth - 60)),
        top: Math.max(40, y - 12),
      }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      Comment
    </button>
  );
}

/** One supervisor edit: what it adds or removes, with the author's Keep (tick) and Revert (X). */
export function EditCard({
  who,
  photo,
  label,
  hunk,
  onKeep,
  onRevert,
}: {
  who: string;
  photo?: string;
  label: string;
  hunk: EditHunk;
  onKeep?: () => void;
  onRevert?: () => void;
}) {
  // the words themselves: a run that starts or ends on a space or a full stop reads without it
  const tidy = (t: string) => t.replace(/^[\s.,;:]+|[\s,;:]+$/g, '') || t.trim();
  const del = tidy(hunk.del);
  const add = tidy(hunk.add);
  const what = del && add ? 'Replace' : add ? 'Add' : 'Delete';
  return (
    <div className={`tc-mc tc-mc-edit${onKeep ? ' needsact' : ''}`} data-cm-card="">
      <CardHead
        who={who}
        photo={photo}
        sub="Edit"
        tag={label}
        onKeep={onKeep}
        keepLabel="Keep this edit"
        onX={onRevert}
        xLabel="Revert this edit"
      />
      <p className="tc-mc-text">
        <b>{what}:</b> {del && <del className="tc-del">{del}</del>}
        {del && add && ' '}
        {add && <ins className="tc-add">{add}</ins>}
      </p>
    </div>
  );
}

/**
 * ehr#571 (reopen 10-27, Chase ask 2; PROGRESS-NOTE V141 V94 / V100 / V101): a change to a field
 * that isn't a text box, beside the field: who made it, "Field change", the part it is in on the
 * same row, and "Changed Start time from 9:02 AM to 10:04 AM". ehr#741 (Chase 10-30, point 1):
 * the green check keeps it (ticked, the card dims), the red X puts the field back as it was, and
 * replies go under it. No "not sent yet" line (point 1B).
 * ehr#741 (Chase 10-31, points 4 and 8; V132 part 2): no "Field change" line and no section pill;
 * the card's colour is its state: white while waiting, green once kept (check), red once not kept
 * (X). The pressed button is boxed; nothing else on the card changes.
 */
export function FieldChangeCard({
  who,
  photo,
  label,
  text,
  comment,
  onKeep,
  state = 'pending',
  onRevert,
  replies = [],
  onReply,
  half,
}: {
  who: string;
  photo?: string;
  /** The part the field is in (Session details, Diagnosis, Mental status), for the buttons' names. */
  label: string;
  text: string;
  /** ehr#890 (point 4): the Added or the Removed card of a list's change. */
  half?: 'add' | 'rem';
  /** A comment saved on the card before replies (ehr#571), shown under the line. */
  comment?: string;
  onKeep?: () => void;
  state?: CardState;
  onRevert?: () => void;
  replies?: ShownReply[];
  onReply?: (text: string) => void;
}) {
  return (
    <div
      className={`tc-mc tc-fc tc-fc-${state}`}
      data-test="field-change"
      data-cm-card=""
      data-cm-state={state}
      {...(half ? { 'data-cm-half': half } : {})}
    >
      <CardHead
        who={who}
        photo={photo}
        about={`${text} (${label})`}
        onKeep={onKeep}
        keepLabel="Keep this change"
        kept={state === 'kept'}
        onX={onRevert}
        xLabel="Revert this change"
        denied={state === 'denied'}
      />
      <p className="tc-mc-text tc-fc-text">{text}</p>
      {comment && <p className="tc-fc-cmt">{comment}</p>}
      <CardReplies
        replies={replies}
        onReply={state === 'pending' ? onReply : undefined}
        label={text}
      />
    </div>
  );
}
